/**
 * useServicesAdmin — данные и мутации «EO → Конфигуратор → Услуги» (A38:
 * справочник общесетевых услуг поверх master_classes; эндпоинт и wire — зона
 * Codex, НЕ переименовываются). Список — useAppData.masterClasses; свой fetch
 * — справочники, связки service_consumables/service_resources и mc_prices.
 * Мутации — run-паттерн useQuestsAdmin (optimistic → flash/rollback+hint →
 * reload); создание — POST c branch_id:'' (A41) и id формы (идемпотентный
 * повтор); удаление — deferred DELETE с keepalive. Провал скоупа блокирует
 * его сейвы (blocked) — bulk/полнострочный POST перетёрли бы БД; chainRef.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import {
  ApiException, type ApiList, type ApiOk, type Consumable, type MasterClass,
  type McPrice, type Resource, type ServiceConsumable, type ServiceResource,
} from "../api/types";
import { useAppData } from "./useAppData";
import { KQ, type Cells, type PriceField } from "./usePricesAdmin";
import type { ConsItem } from "./useQuestsAdmin";

/** Полный драфт формы услуги. id — optimistic-id (один на сессию формы);
 *  dur — минуты, 0 = «нет» (A39); crew — состав A31 «МЖН…», '' = снять. */
export interface ServiceDraft {
  id: string; name: string; short: string; dur: number; crew: string;
  cons: ConsItem[]; res: string[];
}

interface Fetched {
  consCat: Consumable[]; resCat: Resource[];
  cons: Record<string, ConsItem[]>; res: Record<string, string[]>;
  mc: Record<string, McPrice>; loaded: boolean;
  ok: { sc: boolean; sr: boolean; mp: boolean } | null;
}
const F0: Fetched = { consCat: [], resCat: [], cons: {}, res: {}, mc: {}, loaded: false, ok: null };

export interface ServicesAdmin {
  services: MasterClass[];
  consCat: Consumable[]; resCat: Resource[];
  consOf: (id: string) => ConsItem[]; resOf: (id: string) => string[];
  price: (id: string) => Cells;
  loaded: boolean; loadErr: boolean; hint: string | null;
  /** flashOk карточки `svc:{id}` + счётчик n. */
  flash: { key: string; n: number } | null;
  create: (d: ServiceDraft) => Promise<string | null>;
  save: (m: MasterClass, d: ServiceDraft) => Promise<boolean>;
  /** Тройка undo-снекбара (A43.2): ✕ прячет сразу (hide, DELETE отложен),
   *  «Отменить» возвращает без сети (unhide), истечение шлёт removeNow. */
  hide: (id: string) => void;
  unhide: (id: string) => void;
  removeNow: (m: MasterClass) => void;
  /** Авто-сейв ячейки цены (A38): полнострочный POST /api/prices/mc. */
  savePrice: (id: string, f: PriceField, v: number) => void;
  refetch: () => void;
}

const wireCons = (rows: ServiceConsumable[]): Record<string, ConsItem[]> => {
  const out: Record<string, ConsItem[]> = {};
  for (const r of rows) {
    (out[r.service_id] ??= []).push({ consumable_id: r.consumable_id, qty: r.qty, per_player: r.per_player });
  }
  return out;
};
const wireRes = (rows: ServiceResource[]): Record<string, string[]> => {
  const out: Record<string, string[]> = {};
  for (const r of rows) (out[r.service_id] ??= []).push(r.resource_id);
  return out;
};
const got = <T>(r: PromiseSettledResult<ApiList<T>>): T[] | null =>
  (r.status === "fulfilled" ? r.value.results ?? [] : null);
const drop = <T>(o: Record<string, T>, id: string): Record<string, T> => { const n = { ...o }; delete n[id]; return n; };

export function useServicesAdmin(): ServicesAdmin {
  const app = useAppData();
  const [data, setData] = useState<Fetched>(F0);
  const [mcOver, setMcOver] = useState<Record<string, Partial<MasterClass>>>({});
  const [consOver, setConsOver] = useState<Record<string, ConsItem[]>>({});
  const [resOver, setResOver] = useState<Record<string, string[]>>({});
  const [priceOver, setPriceOver] = useState<Record<string, Partial<Cells>>>({});
  const [phantom, setPhantom] = useState<MasterClass[]>([]);
  const [gone, setGone] = useState<string[]>([]);
  const [hint, setHint] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ key: string; n: number } | null>(null);
  const [tick, setTick] = useState(0);
  const pendingRef = useRef(0);
  const chainRef = useRef<Promise<unknown>>(Promise.resolve());
  const flashT = useRef(0);

  useEffect(() => {
    let dead = false;
    void Promise.allSettled([
      api<ApiList<Consumable>>("/api/consumables"),
      api<ApiList<Resource>>("/api/resources"),
      api<ApiList<ServiceConsumable>>("/api/service-consumables"),
      api<ApiList<ServiceResource>>("/api/service-resources"),
      api<ApiList<McPrice>>("/api/prices/mc"),
    ]).then(([rc, rr, rsc, rsr, rmp]) => {
      if (dead) return;
      const [cc, rcat, sc, sr, mp] = [got(rc), got(rr), got(rsc), got(rsr), got(rmp)] as const;
      setData((old) => ({
        consCat: cc ? cc.filter((x) => x.quest_id == null && x.active !== 0) : old.consCat,
        resCat: rcat ?? old.resCat,
        cons: sc ? wireCons(sc) : old.cons,
        res: sr ? wireRes(sr) : old.res,
        mc: mp ? Object.fromEntries(mp.map((p) => [p.mc_id, p])) : old.mc,
        loaded: old.loaded || !!(cc || rcat || sc || sr || mp),
        ok: { sc: !!sc || !!old.ok?.sc, sr: !!sr || !!old.ok?.sr, mp: !!mp || !!old.ok?.mp },
      }));
      /* Полная свежая правда и мутаций в полёте нет — optimistic не нужен.
         31.08: mcOver здесь НЕ трогаем — истина полей услуги приезжает из
         app.reload() (другой конвейер, обычно медленнее): вайп по своим пяти
         GET'ам визуально откатывал только что сохранённую правку на старую. */
      if (pendingRef.current === 0 && cc && rcat && sc && sr && mp) {
        setConsOver({}); setResOver({}); setPriceOver({});
      }
    });
    return () => { dead = true; };
  }, [tick]);
  useEffect(() => () => window.clearTimeout(flashT.current), []);

  /* 31.08: mcOver[id] снимается, когда app.masterClasses реально привёз строку
     с сохранёнными значениями — до тех пор оверлей и есть истина на экране. */
  useEffect(() => {
    if (pendingRef.current > 0) return;
    setMcOver((o) => {
      const ids = Object.keys(o);
      if (!ids.length) return o;
      const n = { ...o };
      let changed = false;
      for (const id of ids) {
        const row = app.masterClasses.find((m) => m.id === id) as Record<string, unknown> | undefined;
        if (row && Object.entries(n[id]!).every(([k, v]) => (row[k] ?? null) === (v ?? null))) {
          delete n[id];
          changed = true;
        }
      }
      return changed ? n : o;
    });
  }, [app.masterClasses]);

  const doFlash = (key: string) => {
    setFlash((f) => ({ key, n: (f?.n ?? 0) + 1 }));
    window.clearTimeout(flashT.current);
    flashT.current = window.setTimeout(() => setFlash(null), 900);
  };

  /** Скоуп не загрузился: replace-set/полнострочный POST перетёр бы БД. */
  const blocked = (s: "sc" | "sr" | "mp"): boolean => {
    if (!data.ok?.[s]) setHint("Данные услуг не загрузились — потяните вниз, чтобы обновить. Правка не сохранена.");
    return !data.ok?.[s];
  };

  /** Обёртка мутации (useQuestsAdmin): flash/rollback+hint, reload в finally. */
  const run = (fn: () => Promise<string | null>, rollback: () => void): Promise<boolean> => {
    setHint(null);
    pendingRef.current += 1;
    return fn()
      .then((key) => { if (key) doFlash(key); return true; })
      .catch((err: unknown) => {
        rollback();
        setHint(err instanceof ApiException && err.status === 404
          ? "Не сохранилось: уже удалено другим пользователем. Список обновлён."
          : "Не сохранилось: ошибка сети. Значение возвращено.");
        return false;
      })
      .finally(() => { pendingRef.current -= 1; app.reload(); setTick((n) => n + 1); });
  };

  const services = app.masterClasses
    .filter((m) => m.active)
    .concat(phantom.filter((p) => !app.masterClasses.some((m) => m.id === p.id)))
    .filter((m) => !gone.includes(m.id))
    .map((m) => (mcOver[m.id] ? { ...m, ...mcOver[m.id] } : m));

  const consOf = useCallback((id: string) => consOver[id] ?? data.cons[id] ?? [], [consOver, data.cons]);
  const resOf = useCallback((id: string) => resOver[id] ?? data.res[id] ?? [], [resOver, data.res]);
  const price = useCallback((id: string): Cells => {
    const p = data.mc[id];
    const base: Partial<Record<PriceField, number | undefined>> = {
      pn: p?.threshold, pb: p?.base_price, pp: p?.extra_per_player, mn: p?.min_price,
    };
    const out = {} as Cells;
    for (const f of KQ) out[f] = priceOver[id]?.[f] ?? base[f] ?? 0;
    return out;
  }, [data.mc, priceOver]);

  const create = async (d: ServiceDraft): Promise<string | null> => {
    const id = d.id; // один на сессию формы: повтор «Создать» — INSERT OR REPLACE, не дубль
    setPhantom((p) => [...p.filter((x) => x.id !== id), {
      id, name: d.name, branch_id: "", duration: d.dur, active: 1,
      short_name: d.short, operators_comp: d.crew || null, sort_order: 0,
    }]);
    setConsOver((o) => ({ ...o, [id]: d.cons }));
    setResOver((o) => ({ ...o, [id]: d.res }));
    let posted = false;
    const ok = await run(async () => {
      /* A41: новая услуга общесетевая — branch_id сентинел ''. */
      /* 31.08: POST c фиксированным id — INSERT OR REPLACE, повтор безопасен */
      await api<ApiOk>("/api/master-classes", { method: "POST", retries: 2, body: {
        id, name: d.name, short_name: d.short, duration: d.dur,
        operators_comp: d.crew, branch_id: "",
      } });
      posted = true;
      /* Новый id: replace-set пустого скоупа безопасен и без загрузки;
         retries — bulk идемпотентен, обрыв сети не должен терять состав. */
      if (d.cons.length) await api<ApiOk>("/api/service-consumables/bulk", { method: "PUT", retries: 2, body: { service_id: id, items: d.cons } });
      if (d.res.length) await api<ApiOk>("/api/service-resources/bulk", { method: "PUT", retries: 2, body: { service_id: id, resource_ids: d.res } });
      return `svc:${id}`;
    }, () => { if (!posted) setPhantom((p) => p.filter((x) => x.id !== id)); });
    if (ok || !posted) return ok ? id : null;
    /* POST прошёл, bulk упал: услуга УЖЕ в БД — честный hint, id возвращаем. */
    /* 31.08: честно — драфт состава при этом уже потерян, «Сохранить» в
       пустой карточке закрепил бы пустоту. */
    setHint("Услуга создана, но расходники/ресурсы не сохранились — откройте карточку и добавьте их заново.");
    return id;
  };

  const save = (m: MasterClass, d: ServiceDraft): Promise<boolean> => {
    /* 31.08: упавший GET связок раньше молча блокировал ЛЮБОЙ «Сохранить»
       (включая чистую правку имени), а форма всё равно закрывалась и правка
       исчезала. Теперь поля сохраняются всегда; replace-set связок — только
       при живом скоупе (иначе bulk перетёр бы БД пустотой). */
    const okCons = !!data.ok?.sc;
    const okRes = !!data.ok?.sr;
    const patch: Partial<MasterClass> = {
      name: d.name, short_name: d.short, duration: d.dur, operators_comp: d.crew || null,
    };
    setMcOver((o) => ({ ...o, [m.id]: patch }));
    setConsOver((o) => ({ ...o, [m.id]: d.cons }));
    setResOver((o) => ({ ...o, [m.id]: d.res }));
    return run(async () => {
      await api<ApiOk>(`/api/master-classes/${encodeURIComponent(m.id)}`, { method: "PUT", retries: 2, body: {
        name: d.name, short_name: d.short, duration: d.dur, operators_comp: d.crew,
      } });
      if (okCons) await api<ApiOk>("/api/service-consumables/bulk", { method: "PUT", retries: 2, body: { service_id: m.id, items: d.cons } });
      if (okRes) await api<ApiOk>("/api/service-resources/bulk", { method: "PUT", retries: 2, body: { service_id: m.id, resource_ids: d.res } });
      if (!okCons || !okRes) setHint("Поля сохранены, но расходники/ресурсы не загрузились и НЕ записаны — потяните вниз и сохраните ещё раз.");
      return `svc:${m.id}`;
    }, () => { // откат ВСЕГО драфта (и cons/res); гард по ссылке — поверх нет новее
      setMcOver((o) => (o[m.id] === patch ? drop(o, m.id) : o));
      setConsOver((o) => (o[m.id] === d.cons ? drop(o, m.id) : o));
      setResOver((o) => (o[m.id] === d.res ? drop(o, m.id) : o));
    });
  };

  const hide = (id: string) => setGone((g) => [...g, id]);
  const unhide = (id: string) => setGone((g) => g.filter((x) => x !== id));

  const removeNow = (m: MasterClass) => {
    void run(async () => {
      /* keepalive: DELETE отложен undo-снекбаром (A43.2) и может уходить при
         выгрузке страницы (pagehide/скрытие TMA) — обязан пережить её. */
      await api<ApiOk>(`/api/master-classes/${encodeURIComponent(m.id)}`, { method: "DELETE", keepalive: true });
      return null;
    }, () => unhide(m.id));
  };

  const savePrice = (id: string, f: PriceField, v: number) => {
    const cur = price(id);
    if (v === cur[f] || blocked("mp")) return; // noop — эталон setCel
    setPriceOver((o) => ({ ...o, [id]: { ...o[id], [f]: v } }));
    const next = { ...cur, [f]: v };
    setHint(null);
    pendingRef.current += 1;
    chainRef.current = chainRef.current
      /* Без флэша (эталон js:232-238) — doFlash затёр бы svc-флэш карточки. */
      .then(() => api<ApiOk>("/api/prices/mc", { method: "POST", retries: 2, body: { prices: [{
        mc_id: id, base_price: next.pb, threshold: next.pn, extra_per_player: next.pp, min_price: next.mn,
      }] } }))
      .catch(() => {
        setPriceOver((o) => (o[id]?.[f] === v // поверх нет новее — откатываем
          ? { ...o, [id]: { ...o[id], [f]: undefined } } : o));
        setHint("Не сохранилось: ошибка сети. Значение возвращено.");
      })
      .finally(() => { pendingRef.current -= 1; setTick((n) => n + 1); });
  };

  return {
    services, consCat: data.consCat, resCat: data.resCat, consOf, resOf, price,
    loaded: data.loaded, loadErr: data.ok != null && !(data.ok.sc && data.ok.sr && data.ok.mp),
    hint, flash, create, save, hide, unhide, removeNow, savePrice,
    refetch: () => setTick((n) => n + 1),
  };
}
